import type { PlanungRow } from "@/lib/montageplanung";
import { addDays, formatDateDe, isNonWorkDay, toIso } from "./dateUtils";

export interface AblagePlacement {
  rowIds: number[];
  dateFrom: string;
  dateTo: string;
  /** Alle angeforderten Personen haben eine Zeile unter dem Ablagepunkt. */
  complete: boolean;
}

/** Nächste `dayCount` Arbeitstage ab `anchor`, Start selbst auf einen Arbeitstag gezogen. */
export function spanWorkDays(
  anchor: Date,
  dayCount: number,
  nonWorkWeekdays?: number[] | null,
): { from: Date; to: Date } {
  const count = Math.max(1, dayCount);
  let cursor = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  for (let i = 0; i < 21 && isNonWorkDay(cursor, nonWorkWeekdays); i++) {
    cursor = addDays(cursor, 1);
  }
  const from = cursor;
  let remaining = count;
  let last = from;
  let guard = 0;
  while (remaining > 0 && guard < 800) {
    if (!isNonWorkDay(cursor, nonWorkWeekdays)) {
      last = cursor;
      remaining -= 1;
    }
    guard += 1;
    if (remaining > 0) cursor = addDays(cursor, 1);
  }
  return { from, to: last };
}

/** Mitarbeiterzeilen ab der Zielzeile nach unten, Trennlinien und Ressourcen werden übersprungen. */
export function targetEmployeeRows(
  rows: PlanungRow[],
  anchorRowId: number,
  personCount: number,
): PlanungRow[] {
  const start = rows.findIndex((row) => row.id === anchorRowId);
  if (start < 0 || personCount < 1) return [];
  const targets: PlanungRow[] = [];
  for (let i = start; i < rows.length && targets.length < personCount; i++) {
    const row = rows[i];
    if (row && row.kind === "employee") targets.push(row);
  }
  return targets;
}

export function placeAblage(
  rows: PlanungRow[],
  anchorRowId: number,
  anchorDate: Date,
  personCount: number,
  dayCount: number,
): AblagePlacement | null {
  const targets = targetEmployeeRows(rows, anchorRowId, personCount);
  if (targets.length === 0) return null;
  const range = spanWorkDays(anchorDate, dayCount, targets[0]?.non_work_weekdays);
  return {
    rowIds: targets.map((row) => row.id),
    dateFrom: toIso(range.from),
    dateTo: toIso(range.to),
    complete: targets.length === personCount,
  };
}

export function ablageDragHint(
  personCount: number,
  dayCount: number,
  placement: AblagePlacement | null,
): string {
  if (!placement) {
    return "Auf eine Mitarbeiterzeile im Plan ziehen.";
  }
  if (!placement.complete) {
    return `Nur ${placement.rowIds.length} von ${personCount} Personen passen unter diese Zeile.`;
  }
  const from = formatDateDe(placement.dateFrom);
  const to = formatDateDe(placement.dateTo);
  const range = placement.dateFrom === placement.dateTo ? from : `${from} – ${to}`;
  return `${personCount} Personen, ${dayCount} Arbeitstage (${range}). Loslassen zum Einplanen.`;
}
